import { getHours, getMinutes, parse } from 'date-fns';
import { InfoIcon } from 'lucide-react';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Container } from '@/components/chariot-ui';
import { ToggleSwitch } from '@/components/legacy-ui/ToggleSwitch';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useScanCadence } from '@/hooks/useScanCadence';
import { useScanSchedule } from '@/hooks/useScanSchedule';
import { useUpdateSetting } from '@/hooks/useSetting';
import { clampDayOfMonth } from '@/sections/settings/components/forms/scanScheduleSentence';
import type { CadenceFrequency, ScanCadenceBase } from '@/types';
import { WeekDay, WeekDayOptions } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';
import {
  convertDayToUTC,
  convertToUTC,
  getCurrentTimeZone,
} from '@/utils/date.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

/**
 * The value mirrored to a parent (the wizard) via `onChange` when `embedded`.
 * Times are the user-facing (selected-timezone) values, matching internal state.
 */
export interface ScanScheduleValue {
  days: WeekDay[];
  start?: string; // "HH:MM"
  end?: string; // "HH:MM"
  manualBypass: boolean;
  timezone: DropdownOption;
  /** True when the window spans the whole day (no explicit start/end). */
  allDay: boolean;
}

/** The UTC payload `submit()` actually wrote — lets the wizard derive cadence. */
export interface ScanScheduleSubmitResult {
  days: WeekDay[]; // UTC-converted weekdays
  start: string; // UTC "HH:MM"
  end: string; // UTC "HH:MM"
}

export interface ScanScheduleHandle {
  /**
   * Computes the final UTC `{days, start, end}` from current state WITHOUT
   * writing anything. Returns null when invalid. Lets the wizard derive the
   * nested cadence payload from the same tz-conversion source of truth the
   * schedule write uses.
   */
  computeUtc: () => ScanScheduleSubmitResult | null;
  /**
   * Writes the combined `schedule` document (silent), nesting `cadence` when
   * provided. Returns the UTC payload on success, null on failure/invalid.
   */
  submit: (
    cadence?: ScanCadenceBase
  ) => Promise<ScanScheduleSubmitResult | null>;
}

export interface ScanScheduleProps {
  customerEmail?: string;
  onClose?: () => void;
  /** When true: no <h1>, no internal Save/Cancel/Reset buttons (wizard owns chrome). */
  embedded?: boolean;
  /** Fires when (start && end && timezone && days.length > 0) validity changes. */
  onValidityChange?: (valid: boolean) => void;
  /** Mirrors internal state to the wizard for the live summary band. */
  onChange?: (value: ScanScheduleValue) => void;
  /** Drives Step-2 label/help/tag swaps (FIDELITY F3/F4/F5). */
  frequency?: CadenceFrequency;
  /** The SAVED cadence at wizard open — stable for the whole wizard session.
   *  Used to detect a switch TO "daily" this session (vs opening already on
   *  daily). ScanSchedule remounts on each entry into Step 2, so a prev-vs-current
   *  transition is invisible here; comparing `frequency` against this stable value
   *  is the only reliable signal. Ignored when not embedded. */
  initialFrequency?: CadenceFrequency;
  /** Controlled manual-bypass value for embedded (wizard) mode; the wizard owns
   *  this state and renders the toggle in its own step. Ignored when not embedded. */
  manualBypass?: boolean;
  /** Mirrors manual-bypass changes back to the wizard (embedded mode), so the
   *  wizard's `manualBypass` state — which the save path reads — stays in sync. */
  onManualBypassChange?: (next: boolean) => void;
  /** Controlled day-of-month (1-31) for embedded (wizard) mode; the wizard owns
   *  this state (it writes the nested cadence). `31` is the "Last day" sentinel
   *  (backend clamps to each month's true last day). Only the monthly/quarterly
   *  grid reads it; ignored when not embedded. Mirrors `manualBypass`. */
  dayOfMonth?: number;
  /** Mirrors day-of-month changes back to the wizard (embedded mode), so the
   *  wizard's `dayOfMonth` state — which the cadence save reads — stays in sync. */
  onDayOfMonthChange?: (next: number) => void;
  /** "Any time" mode: no launch window. Hides the weekday/time/timezone pickers
   *  (monthly/quarterly keep the day-of-month grid), reports valid unconditionally,
   *  and `submit()` writes a cadence-only document (no days/start/end). */
  anyTime?: boolean;
}

const parseTime = (timeString: string): { hour: number; minute: number } => {
  const [hour, minute] = timeString.split(':').map(Number);
  return { hour, minute };
};

const isWeeklyish = (f?: CadenceFrequency) =>
  f === undefined || f === 'daily' || f === 'weekly' || f === 'biweekly';

export const ScanSchedule = forwardRef<ScanScheduleHandle, ScanScheduleProps>(
  function ScanSchedule(
    {
      customerEmail,
      onClose,
      embedded,
      onValidityChange,
      onChange,
      frequency,
      initialFrequency,
      manualBypass: controlledManualBypass,
      onManualBypassChange,
      dayOfMonth: controlledDayOfMonth,
      onDayOfMonthChange,
      anyTime,
    },
    ref
  ) {
    const scanTimeOptions = useMemo(() => {
      const options = [];

      [...Array(12).keys()].forEach(i => {
        const hour = i <= 9 ? `0${i}` : `${i}`;
        options.push({ label: `${hour}:00 AM`, value: `${hour}:00` });
      });

      options.push({ label: '12:00 PM', value: '12:00' });

      [...Array(11).keys()].forEach(i => {
        const hour = i < 9 ? `0${i + 1}` : `${i + 1}`;
        options.push({ label: `${hour}:00 PM`, value: `${13 + i}:00` });
      });

      return options;
    }, []);
    const { offset, tz } = getCurrentTimeZone();
    const {
      currentScanSchedule,
      isUpdatingScanSchedule,
      updateScanScheduleAsync,
      deleteScanSchedule,
    } = useScanSchedule({ email: customerEmail });
    // Silent writer for embedded mode (the wizard owns the combined toast).
    const { mutateAsync: updateSettingSilent } =
      useUpdateSetting(customerEmail);
    // Cadence writer (PUTs the `scan-cadence` Configuration). Scan cadence is
    // Praetorian-only: the wizard is card-gated to Praetorian users, and the
    // backend enforces EntitlementPraetorian on the /configuration write (403
    // for non-Praetorian), which propagates to the wizard's try/catch.
    const { updateScanCadenceAsync } = useScanCadence({ email: customerEmail });
    const timezoneOptions = [
      {
        label: '(GMT+00:00) UTC',
        value: 'UTC',
      },
      {
        label: `(${offset}) ${tz}`,
        value: tz,
      },
    ];

    const [scanDays, setScanDays] = useState<WeekDay[]>(
      currentScanSchedule?.days || []
    );
    const [start, setStart] = useState<DropdownOption | undefined>(
      scanTimeOptions.find(option => option.value === currentScanSchedule.start)
    );
    const [end, setEnd] = useState<DropdownOption | undefined>(
      scanTimeOptions.find(option => option.value === currentScanSchedule.end)
    );
    const [savedTimezone, setSavedTimezone] = useStorage<DropdownOption>(
      { key: StorageKey.SCAN_SCHEDULE_TIMEZONE },
      timezoneOptions[0] // Default timezone is UTC
    );
    const [timezone, setTimezone] = useState<DropdownOption>(savedTimezone);
    const [manualBypass, setManualBypass] = useState<boolean>(
      currentScanSchedule?.manualBypass ?? false
    );
    // Embedded only: whether to bound the window to a time-of-day range. Off =
    // all-day (24h) on the selected days. A saved bounded window (start != end)
    // opens with it on; a fresh or all-day (start == end) schedule opens off.
    const [limitWindow, setLimitWindow] = useState<boolean>(
      () =>
        !!(
          currentScanSchedule?.start &&
          currentScanSchedule?.end &&
          currentScanSchedule.start !== currentScanSchedule.end
        )
    );
    // Standalone (non-embedded, legacy) always bounds the window; only the
    // embedded wizard offers the all-day default.
    const windowBounded = embedded ? limitWindow : true;

    // `useScanSchedule` rebuilds `currentScanSchedule` (and its `.days` array)
    // with a fresh reference on every render, so this effect's dep changes
    // every render. Guard on the SERIALIZED content of the fields we sync so we
    // only push API data into local state when it actually changes — otherwise
    // setScanDays/setStart/etc. fire a fresh `[]`/option every render and create
    // an infinite render loop (child setState → parent re-render → new
    // `currentScanSchedule` ref → effect re-fires).
    const lastSyncedSchedule = useRef<string | null>(null);

    // Sync state with API data when it arrives
    useEffect(() => {
      if (!currentScanSchedule) return;

      const serialized = JSON.stringify({
        days: currentScanSchedule.days || [],
        start: currentScanSchedule.start,
        end: currentScanSchedule.end,
        manualBypass: currentScanSchedule.manualBypass ?? false,
      });
      if (serialized === lastSyncedSchedule.current) return;
      lastSyncedSchedule.current = serialized;

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setScanDays(currentScanSchedule.days || []);

      // The hook already converts the schedule times to HH:mm format
      const startOption = scanTimeOptions.find(
        option => option.value === currentScanSchedule.start
      );
      const endOption = scanTimeOptions.find(
        option => option.value === currentScanSchedule.end
      );

      if (startOption) setStart(startOption);
      if (endOption) setEnd(endOption);
      setManualBypass(currentScanSchedule.manualBypass ?? false);
      // Keep the window toggle in sync — a persisted bounded window must open
      // with it on, or it would be saved back as an all-day (00:00/00:00) window.
      setLimitWindow(
        !!(
          currentScanSchedule.start &&
          currentScanSchedule.end &&
          currentScanSchedule.start !== currentScanSchedule.end
        )
      );
    }, [currentScanSchedule, scanTimeOptions]);

    // Any-time imposes no window (always valid). A windowed schedule needs ≥1
    // day; times are required only when the window is bounded (all-day needs none).
    const isValid =
      !!anyTime ||
      (scanDays.length > 0 &&
        (!windowBounded || (!!start && !!end && !!timezone)));

    // Notify the wizard of validity changes (F2/I4 gate the Save button).
    // Guard on the actual boolean so we only emit when validity flips — emitting
    // on every render would let the parent's setState feed back into our own
    // re-render and loop.
    const lastEmittedValidity = useRef<boolean | null>(null);
    useEffect(() => {
      if (isValid === lastEmittedValidity.current) return;
      lastEmittedValidity.current = isValid;
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    // Mirror state to the wizard for the live summary band (C4). This effect
    // builds a FRESH object literal, so it must emit only when the emitted
    // VALUE actually changes — otherwise the parent's setState re-renders us,
    // re-fires this effect, and loops. Guard on the serialized value.
    const lastEmittedValue = useRef<string | null>(null);
    useEffect(() => {
      const value: ScanScheduleValue = {
        days: scanDays,
        start: start?.value as string | undefined,
        end: end?.value as string | undefined,
        manualBypass,
        timezone,
        allDay: !windowBounded,
      };
      const serialized = JSON.stringify(value);
      if (serialized === lastEmittedValue.current) return;
      lastEmittedValue.current = serialized;
      onChange?.(value);
    }, [scanDays, start, end, manualBypass, timezone, windowBounded, onChange]);

    function getTimeOptions(type: 'start' | 'end') {
      const options = scanTimeOptions;

      if (isUpdatingScanSchedule) {
        return options.map(currentOption => ({
          ...currentOption,
          disabled: true,
        }));
      }

      if (type === 'start') {
        return options.map(option => ({
          ...option,
          disabled: option.value === start?.value,
        }));
      }
      if (!start) {
        return options.map(option => ({ ...option, disabled: false }));
      }

      // Parse start time
      const startTime = parse(start.value as string, 'HH:mm', new Date());

      // Convert time to minutes since the start of the day
      const toMinutes = (time: Date) => getHours(time) * 60 + getMinutes(time);
      const startMinutes = toMinutes(startTime);
      const minGap = 360; // 6 hours in minutes
      const fullDay = 1440; // 24 hours in minutes

      return options.map(option => {
        const optionTime = parse(option.value, 'HH:mm', new Date());
        const optionMinutes = toMinutes(optionTime);

        const isStartDisabled = option.value === start.value;

        // Disable any time within 6 hours after start time
        let isWithinRestrictedRange = false;

        if (startMinutes + minGap < fullDay) {
          // Normal case: Start time and valid range are within the same day
          isWithinRestrictedRange =
            optionMinutes > startMinutes &&
            optionMinutes < startMinutes + minGap;
        } else {
          // Wrap-around case: Start time is late at night, and end time might wrap into the next day
          const cutoffTime = (startMinutes + minGap) % fullDay;
          isWithinRestrictedRange =
            optionMinutes > startMinutes || optionMinutes < cutoffTime;
        }

        return {
          ...option,
          disabled: isStartDisabled || isWithinRestrictedRange,
        };
      });
    }

    const startItems = getTimeOptions('start').map(o => (
      <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
        {o.label}
      </SelectItem>
    ));
    const endItems = getTimeOptions('end').map(o => (
      <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
        {o.label}
      </SelectItem>
    ));

    useEffect(() => {
      // Seed a default 00:00–23:00 window only for a BOUNDED schedule; all-day
      // (embedded, toggle off) carries no times.
      if (windowBounded && !start && !end && scanDays.length > 0) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setStart(scanTimeOptions.find(o => o.value === '00:00'));
        setEnd(scanTimeOptions.find(o => o.value === '23:00'));
      }
    }, [windowBounded, start, end, scanDays]);

    // Default the launch window to every day across the full 00:00–23:00 UTC range
    // whenever Step 2 bootstraps on a daily cadence with no concrete schedule to
    // preserve (a daily cadence re-scans every day, so a weekday subset would
    // contradict it). Two cases fire this:
    //   1. The user switched the cadence TO "daily" this session. ScanSchedule is
    //      remounted on each entry into Step 2, so a prev-vs-current transition is
    //      invisible here; instead compare against the wizard's stable
    //      initialFrequency (the SAVED cadence). initialFrequency !== 'daily' means
    //      the user changed TO daily this session.
    //   2. The tenant is unset: useScanCadence defaults frequency to 'daily' AND the
    //      card passes initialFrequency='daily', so case 1 is false — but there is no
    //      SAVED schedule (currentScanSchedule.days is []), so without this branch the
    //      tenant would land on Step 2 with zero days and Save disabled, contradicting
    //      the card's advertised "scan daily at 00:00 UTC (all days, all hours)"
    //      default. Gate on the SAVED schedule (currentScanSchedule.days), NOT the
    //      transient local scanDays — the sync effect above populates local state from
    //      a genuinely saved daily subset, and reading saved data here avoids that
    //      effect-ordering race.
    // A genuinely saved daily subset (non-empty saved days, initialFrequency 'daily')
    // satisfies neither case → preserved. Guarded by a ref so it applies once per
    // mount and the user can still deselect days afterward.
    const didApplyDailyDefault = useRef(false);
    const hasSavedScanDays = (currentScanSchedule?.days?.length ?? 0) > 0;
    useEffect(() => {
      if (
        !didApplyDailyDefault.current &&
        frequency === 'daily' &&
        (initialFrequency !== 'daily' || !hasSavedScanDays)
      ) {
        didApplyDailyDefault.current = true;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setScanDays(WeekDayOptions.map(o => o.value));
        if (windowBounded) {
          setStart(scanTimeOptions.find(o => o.value === '00:00'));
          setEnd(scanTimeOptions.find(o => o.value === '23:00'));
        }
      }
    }, [frequency, initialFrequency, hasSavedScanDays, windowBounded]);

    /**
     * Computes the final UTC `{days, start, end}` from current state, applying
     * the exact same tz conversion the standalone Save path uses
     * (convertToUTC / convertDayToUTC). Single source of truth for both the
     * `schedule` write AND the wizard's cadence-payload derivation (§8).
     */
    function computeUtcPayload(): ScanScheduleSubmitResult | null {
      if (scanDays.length === 0) return null;
      // All-day window: defined directly in UTC days, no time bound — timezone is
      // irrelevant when the window spans the whole day. start==end reads as 24h
      // on the backend (Within()'s overnight-wrap branch).
      if (!windowBounded) {
        return { days: scanDays, start: '00:00', end: '00:00' };
      }
      if (!start?.value || !end?.value || !timezone) {
        return null;
      }
      if (timezone.value !== 'UTC') {
        const { utcTime: utcStartTime, diff: diffStart } = convertToUTC(
          start.value as string,
          timezone.value as string
        );
        const { utcTime: utcEndTime } = convertToUTC(
          end.value as string,
          timezone.value as string
        );
        const days = scanDays.map(day => convertDayToUTC(day, diffStart));
        return { days, start: utcStartTime, end: utcEndTime };
      }
      return {
        days: scanDays,
        start: start.value as string,
        end: end.value as string,
      };
    }

    // Imperative handle for the wizard: write the `schedule` document silently
    // and return the UTC payload that was written.
    useImperativeHandle(
      ref,
      () => ({
        computeUtc: () => computeUtcPayload(),
        submit: async (cadence?: ScanCadenceBase) => {
          // Silent writes (the wizard owns toasts). A failed PUT rejects — the
          // wizard catches it so it can surface the backend's specific message.
          // Two separate writes: the window lives in the `schedule` Setting and
          // the cadence in the `scan-cadence` Configuration. The additive
          // cadence Configuration write ALWAYS runs first; the destructive
          // window Setting write runs second. Ordering matters: the window
          // write is a DESTRUCTIVE full-document replace, so if it ran first
          // and the cadence write then failed, the cadence would be lost. By
          // writing the cadence first, a partial failure cannot lose it. The
          // wizard is Praetorian-only (card-gated), and the backend enforces
          // EntitlementPraetorian on the /configuration write (403 for
          // non-Praetorian) — so a stray non-Praetorian write surfaces as the
          // backend's 403 and aborts BEFORE the window Setting is touched. That
          // 403 propagates to the wizard's try/catch (no toast here).
          // Any-time: write a window-less document. PUT replaces the whole
          // document, so omitting days/start/end clears any prior window.
          if (anyTime) {
            if (cadence) {
              await updateScanCadenceAsync(cadence);
            }
            await updateSettingSilent({
              name: 'schedule',
              value: {
                manualBypass: embedded
                  ? (controlledManualBypass ?? false)
                  : manualBypass,
              },
              silent: true,
            });
            return { days: [], start: '', end: '' };
          }
          const payload = computeUtcPayload();
          if (!payload) return null;
          // Persist the selected timezone once, on actual save (kept out of the
          // pure computeUtc() path so it isn't written twice per save).
          setSavedTimezone(timezone);
          if (cadence) {
            await updateScanCadenceAsync(cadence);
          }
          await updateSettingSilent({
            name: 'schedule',
            value: {
              days: payload.days,
              start: parseTime(payload.start),
              end: parseTime(payload.end),
              manualBypass: embedded
                ? (controlledManualBypass ?? false)
                : manualBypass,
            },
            silent: true,
          });
          return payload;
        },
      }),
      // The handle reads current state; recreate when those values change.

      [
        start,
        end,
        timezone,
        scanDays,
        manualBypass,
        controlledManualBypass,
        anyTime,
        embedded,
        windowBounded,
        updateScanCadenceAsync,
      ]
    );

    const weekdayLabel = isWeeklyish(frequency)
      ? 'Days to scan'
      : 'Eligible launch days';

    const weekdayHelp = (() => {
      switch (frequency) {
        case 'daily':
          return 'Pick the day(s) of the week scans may launch. A scan can run once on each selected day.';
        case 'biweekly':
          return 'Pick the day(s) of the week scans may launch. They recur on this rhythm every 2 weeks (the starting week is anchored on save).';
        case 'monthly':
          return "These weekdays don't add more scans — they only choose which window opens the scan. Each month, the first matching window fires once.";
        case 'quarterly':
          return 'These weekdays only choose which window opens the scan. Each quarter (Jan · Apr · Jul · Oct), the first matching window fires once.';
        case 'weekly':
          return 'Pick the day(s) of the week scans may launch.';
        default:
          return '';
      }
    })();

    const showAnchorFrame = !isWeeklyish(frequency);
    const anchorTag =
      frequency === 'quarterly'
        ? 'First eligible window per quarter'
        : 'First eligible window per month';

    // Day-of-month grid (embedded wizard only): the value lives on the wizard
    // (`controlledDayOfMonth`), which writes the nested cadence. `31` is the
    // "Last day" sentinel — selecting either cell 31 or the wide "Last day" chip
    // sets 31; the "Last day" chip is the canonical affordance, so when the value
    // is 31 the chip reads as checked and numeric cell 31 does not (one checked
    // cell, matching the approved mockup). The backend clamps 31 to each month's
    // true last day, so 29/30/31 fall back in shorter months (communicated with
    // a marker dot + help text, never color alone — WCAG 1.4.1).
    const domValue = clampDayOfMonth(controlledDayOfMonth);
    // Roving-tabindex order: numeric cells 1..31, then the "Last day" chip.
    // We model "Last day" as a distinct roving item that resolves to value 31.
    const DOM_LAST_DAY_INDEX = 31; // index 0..30 => days 1..31; index 31 => "Last day"
    // The roving "active" item: the chip when the value is the last-day sentinel,
    // otherwise the numeric cell for the chosen day.
    const domActiveIndex = domValue === 31 ? DOM_LAST_DAY_INDEX : domValue - 1;
    // Roving-tabindex focus targets, indexed by roving item (0..30 numeric cells,
    // 31 = the "Last day" chip). Populated by renderDomCell's ref callback so
    // moveDom can move DOM focus to the next item (WAI-ARIA APG roving tabindex).
    const domCellRefs = useRef<(HTMLDivElement | null)[]>([]);
    const moveDom = (delta: number) => {
      const total = 32; // 31 numeric cells + the Last-day chip
      let nextIdx = (domActiveIndex + delta + total) % total;
      // Index 30 (numeric "31") can never be the active item — value 31 is
      // canonically the "Last day" chip (one checked cell, per the approved
      // mockup). Nudge one step in the direction of travel so ArrowLeft from
      // the chip lands on day 30 instead of dead-ending, and row moves (±7)
      // that hit it settle on the adjacent reachable item.
      if (nextIdx === 30) {
        nextIdx = (nextIdx + Math.sign(delta) + total) % total;
      }
      onDayOfMonthChange?.(nextIdx === DOM_LAST_DAY_INDEX ? 31 : nextIdx + 1);
      domCellRefs.current[nextIdx]?.focus();
    };
    const dayOfMonthLabel =
      frequency === 'quarterly' ? 'Anchor-month day' : 'Day of month';
    const dayOfMonthHelp =
      frequency === 'quarterly'
        ? "Which day of each quarter's anchor month (Jan · Apr · Jul · Oct) the period begins on, in UTC."
        : 'Which day of each month the period begins on, in UTC.';

    // A single numeric day cell or the wide "Last day" chip. `domIndex` is the
    // roving index (0..30 numeric, 31 = chip); `value` is the day it selects.
    const renderDomCell = (
      domIndex: number,
      value: number,
      label: string,
      opts: { lastDay?: boolean; clamps?: boolean } = {}
    ) => {
      const checked = domIndex === domActiveIndex;
      return (
        <div
          key={label}
          ref={el => {
            domCellRefs.current[domIndex] = el;
          }}
          role="radio"
          aria-checked={checked}
          aria-label={
            opts.clamps
              ? `${label}, falls back to the last day in shorter months`
              : label
          }
          tabIndex={checked ? 0 : -1}
          onClick={() => onDayOfMonthChange?.(value)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onDayOfMonthChange?.(value);
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              moveDom(1);
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              moveDom(-1);
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              moveDom(7);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              moveDom(-7);
            }
          }}
          data-testid={
            opts.lastDay
              ? 'scan-schedule-dom-last-day'
              : `scan-schedule-dom-cell-${value}`
          }
          className={cn(
            'relative flex h-7 cursor-pointer items-center justify-center rounded-md border text-xs font-medium transition-colors',
            'focus-visible:outline-chariot-brand-primary focus-visible:outline-2 focus-visible:outline-offset-2',
            opts.lastDay && 'col-span-4 font-bold',
            checked
              ? 'border-chariot-brand-primary bg-chariot-brand-primary text-white'
              : 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:bg-chariot-background-secondary'
          )}
        >
          {label}
          {opts.clamps && (
            <span
              aria-hidden="true"
              className={cn(
                'absolute top-1 right-1 size-1 rounded-full',
                checked ? 'bg-white' : 'bg-chariot-text-secondary'
              )}
            />
          )}
        </div>
      );
    };

    const dayOfMonthSection = (
      <div className="mb-3">
        <div className="flex items-center gap-2 pb-1">
          <h3 className="text-chariot-text-primary text-sm font-bold">
            {dayOfMonthLabel}
          </h3>
          <Tooltip title={dayOfMonthHelp}>
            <InfoIcon className="text-chariot-text-secondary size-4" />
          </Tooltip>
        </div>
        <div
          role="radiogroup"
          aria-label={dayOfMonthLabel}
          className="grid grid-cols-7 gap-1"
          data-testid="scan-schedule-day-of-month-grid"
        >
          {Array.from({ length: 31 }, (_, i) => i + 1).map(day =>
            renderDomCell(day - 1, day, String(day), {
              clamps: day >= 29,
            })
          )}
          {renderDomCell(DOM_LAST_DAY_INDEX, 31, 'Last day', { lastDay: true })}
        </div>
        <p className="text-chariot-text-secondary mt-1.5 text-xs leading-relaxed">
          29, 30, 31 fall back to the last day in shorter months (Feb 28/29, Apr
          30…). &ldquo;Last day&rdquo; always lands on each month&rsquo;s final
          day.
        </p>
      </div>
    );

    // Width/layout trigger: the responsive full-width layout (built for the
    // customer-impersonation modal) must ALSO apply in the embedded wizard,
    // whose dialog is only 720px wide. Without this, a Praetorian operator
    // (customerEmail undefined) falls back to the fixed wide layout and the
    // 7×108px weekday row overflows the dialog. Visibility conditions (e.g. the
    // manual-bypass row's `!customerEmail`) are intentionally NOT folded in here.
    const fitContainer = embedded || !!customerEmail;

    const weekdaySection = (
      <div
        role="group"
        aria-label={weekdayLabel}
        className="grid grid-cols-7 gap-2 pb-4"
      >
        {WeekDayOptions.map(({ label, value }) => {
          const selected = scanDays.includes(value);
          return (
            <button
              key={label}
              type="button"
              role="checkbox"
              aria-checked={selected}
              aria-label={label}
              disabled={isUpdatingScanSchedule}
              onClick={() =>
                setScanDays(prev =>
                  prev.includes(value)
                    ? prev.filter(d => d !== value)
                    : [...prev, value]
                )
              }
              data-testid={`scan-schedule-day-checkbox-${value}`}
              className={cn(
                'flex h-9 items-center justify-center rounded-md border text-sm font-medium transition-colors',
                'focus-visible:ring-ring focus-visible:ring-1 focus-visible:outline-hidden',
                selected
                  ? 'border-chariot-brand-primary bg-chariot-brand-primary text-white'
                  : 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:bg-chariot-background-secondary',
                isUpdatingScanSchedule && 'cursor-not-allowed opacity-50'
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    );

    // "Choose a Time" block. `stacked` switches the start/end selects from the
    // default side-by-side `w-44` boxes (single-column flow) to a full-width
    // vertical stack — the latter is needed inside the narrow right column of
    // the embedded monthly/quarterly two-column layout, where two 176px boxes +
    // a "to" overflow. Testids/aria-labels/SelectContent are identical in both.
    const renderTimeSection = (stacked: boolean) => (
      <>
        <div className="flex items-center gap-2">
          <h2 className="text-md pb-1 font-bold">Choose a Time</h2>
          <Tooltip title="Requires a minimum scan window of 6 hours">
            <InfoIcon className="text-chariot-text-secondary size-4" />
          </Tooltip>
        </div>
        {embedded && (
          <p className="text-chariot-text-secondary pb-2 text-xs">
            Scans may only start while this window is open. The hourly cron
            fires at the window&rsquo;s opening.
          </p>
        )}
        <div
          className={cn(
            stacked ? 'flex' : 'flex gap-10',
            !stacked && fitContainer && 'flex-wrap gap-y-4'
          )}
        >
          <div
            className={cn(
              stacked ? 'flex w-full flex-col gap-2' : 'flex items-center gap-4'
            )}
          >
            <div className={cn(stacked ? 'w-full' : 'w-44')}>
              <Select
                value={(start?.value as string | undefined) ?? ''}
                onValueChange={v => {
                  setStart(scanTimeOptions.find(o => o.value === v));
                  setEnd(undefined);
                }}
                disabled={isUpdatingScanSchedule}
              >
                <SelectTrigger
                  aria-label="Scan window start time"
                  data-testid="scan-schedule-start-time-select"
                >
                  <SelectValue placeholder="Select start time" />
                </SelectTrigger>
                <SelectContent className="max-h-[min(24rem,var(--radix-select-content-available-height))]">
                  {startItems}
                </SelectContent>
              </Select>
            </div>
            {stacked ? (
              <div
                className="text-chariot-text-secondary flex items-center gap-2 text-xs"
                aria-hidden="true"
              >
                <span className="border-chariot-stroke flex-1 border-t" />
                to
                <span className="border-chariot-stroke flex-1 border-t" />
              </div>
            ) : (
              <p className="font-bold">to</p>
            )}
            <div className={cn(stacked ? 'w-full' : 'w-44')}>
              <Select
                value={(end?.value as string | undefined) ?? ''}
                onValueChange={v =>
                  setEnd(scanTimeOptions.find(o => o.value === v))
                }
                disabled={isUpdatingScanSchedule || !start}
              >
                <SelectTrigger
                  aria-label="Scan window end time"
                  data-testid="scan-schedule-end-time-select"
                >
                  <SelectValue placeholder="Select end time" />
                </SelectTrigger>
                <SelectContent className="max-h-[min(24rem,var(--radix-select-content-available-height))]">
                  {endItems}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </>
    );

    const timezoneSection = (
      <div className="mt-4">
        <div className="flex items-center gap-2 pb-1">
          <h2 className="text-md font-bold">Timezone</h2>
          {embedded && (
            <Tooltip
              title={
                <span>
                  Times are stored and shown in <b className="font-bold">UTC</b>
                  . A minimum <b className="font-bold">6-hour</b> window is
                  required. Choosing a non-UTC timezone converts to UTC on save.
                </span>
              }
            >
              <InfoIcon className="text-chariot-text-secondary size-4" />
            </Tooltip>
          )}
        </div>
        <div className="w-56">
          <Select
            value={(timezone?.value as string | undefined) ?? ''}
            onValueChange={v =>
              setTimezone(
                timezoneOptions.find(o => o.value === v) ?? timezoneOptions[0]
              )
            }
            disabled={isUpdatingScanSchedule}
          >
            <SelectTrigger
              aria-label="Scan window timezone"
              data-testid="scan-schedule-timezone-select"
            >
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent className="max-h-[min(24rem,var(--radix-select-content-available-height))]">
              {timezoneOptions
                // The local-tz option collapses onto the UTC option for
                // UTC-region users; dedupe by value so Radix gets unique
                // item values (and React unique keys).
                .filter(
                  (o, i, arr) => arr.findIndex(x => x.value === o.value) === i
                )
                .map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );

    // Embedded time portion: an all-day default with a "Limit to a time window"
    // toggle that reveals the start/end (+ timezone) pickers. Off => the window
    // spans the whole selected day(s); on => a bounded time-of-day window with
    // the 6-hour minimum. The standalone (non-embedded) form always bounds and
    // renders the time/timezone sections directly instead.
    const renderWindowTimePortion = (stacked: boolean) => (
      <>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-md font-bold">Limit to a time window</h2>
            <Tooltip title="Off: scans may launch any time on the selected day(s). On: scans launch only within a time-of-day window (minimum 6 hours).">
              <InfoIcon className="text-chariot-text-secondary size-4" />
            </Tooltip>
          </div>
          <ToggleSwitch
            checked={limitWindow}
            onChange={setLimitWindow}
            disabled={isUpdatingScanSchedule}
            ariaLabel="Toggle time-of-day window"
            data-testid="scan-schedule-limit-window-toggle"
          />
        </div>
        {limitWindow ? (
          <div className="mt-3">
            {renderTimeSection(stacked)}
            {timezoneSection}
          </div>
        ) : (
          <p className="text-chariot-text-secondary mt-2 text-sm">
            Scans may launch any time on the selected day(s).
          </p>
        )}
      </>
    );

    // Manual-bypass row: hidden only when a customer is being edited (parity).
    // Renders in BOTH the standalone form and the embedded wizard (Step 2). When
    // embedded, the wizard owns the value via the `manualBypass` prop (read as
    // `controlledManualBypass`) and the save path reads the wizard's state; the
    // toggle mirrors changes back via `onManualBypassChange`. The standalone form
    // keeps its own local `manualBypass` state. `divider` is true for the
    // full-width single-column flow (full-bleed top border) and false in the
    // narrow right column of the two-column layout, where a full-bleed rule looks
    // orphaned — there a plain top margin separates it instead.
    const renderManualBypassSection = (divider: boolean) =>
      !customerEmail ? (
        <>
          {divider ? (
            <div className="border-chariot-stroke my-4 border-t" />
          ) : (
            <div className="mt-4" />
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-md font-bold">
                {embedded
                  ? 'Manual scans bypass the schedule'
                  : 'Manual scans bypass schedule'}
              </h2>
              <Tooltip
                title={
                  embedded
                    ? 'When enabled, manually triggered scans run immediately, ignoring both the cadence period and the time window.'
                    : 'When enabled, manually triggered scans will run immediately regardless of the scan schedule.'
                }
              >
                <InfoIcon className="text-chariot-text-secondary size-4" />
              </Tooltip>
            </div>
            <ToggleSwitch
              checked={
                embedded ? (controlledManualBypass ?? false) : manualBypass
              }
              onChange={next => {
                setManualBypass(next);
                if (embedded) onManualBypassChange?.(next);
              }}
              disabled={isUpdatingScanSchedule}
              ariaLabel="Toggle manual scan bypass"
              data-testid="scan-schedule-manual-bypass-toggle"
            />
          </div>
        </>
      ) : null;

    // The date-anchoring controls for the embedded monthly/quarterly case: the
    // day-of-month grid, the "Eligible launch weekday(s)" sub-heading, and the
    // weekday grid, all inside the brand-accent anchor frame. Built as a local so
    // it can sit in the left cell of the two-column layout.
    const anchorFrameSection = (
      <div className="border-chariot-brand-primary border-l-[3px] pl-3">
        <div className="text-chariot-brand-primary mb-1.5 text-xs font-bold tracking-wide uppercase">
          {anchorTag}
        </div>
        {/* Day-of-month grid sits at the TOP of the anchor frame, above the
            weekday "launch window" grid. Renders only for monthly/quarterly
            (showAnchorFrame === !isWeeklyish). Its own heading ("Day of
            month"/"Anchor-month day") + the weekday sub-heading below
            disambiguate the two 7-col grids so they don't read as one
            control (C-B1). */}
        {dayOfMonthSection}
        <div className="flex items-center gap-2 pb-1">
          <h3 className="text-chariot-text-primary text-sm font-bold">
            Eligible launch weekday(s)
          </h3>
          {weekdayHelp && (
            <Tooltip title={weekdayHelp}>
              <InfoIcon className="text-chariot-text-secondary size-4" />
            </Tooltip>
          )}
        </div>
        {weekdaySection}
      </div>
    );

    return (
      <div className="flex flex-col" data-testid="scan-schedule-form">
        {!embedded && <h1 className="text-2xl font-bold">Scan Schedule</h1>}
        {!embedded && (
          <p>
            Select a default scan schedule that will be{' '}
            <span className="font-bold italic">applied to all assets</span>.
            This determines how often Chariot scans your assets.
          </p>
        )}
        <Container
          className={cn(
            'bg-chariot-background-primary my-4 w-fit px-6 py-4',
            fitContainer && 'w-full'
          )}
        >
          {/* Days to scan — label/help swap by cadence frequency (F3/F4/F5).
              For the embedded monthly/quarterly case the anchor-frame grids are
              tall, so the date-anchoring controls and the time/timezone/manual
              blocks are laid out in two columns (left: anchor frame, right: the
              required time selects + timezone + manual bypass) so the time
              selects sit BESIDE the grid instead of being pushed below the
              480px wizard fold. Every other case keeps the single-column flow
              byte-for-byte. */}
          {anyTime ? (
            <div data-testid="scan-schedule-any-time">
              {/* Cadence-only: no launch window. Monthly/quarterly still need the
                  day-of-month anchor; weekly/biweekly/daily need nothing. */}
              {showAnchorFrame && dayOfMonthSection}
              <p className="text-chariot-text-secondary text-sm">
                Scans run whenever they&rsquo;re due — any day and time. Choose
                <span className="font-bold"> Specific window </span>
                to restrict when scans may launch.
              </p>
              {renderManualBypassSection(true)}
            </div>
          ) : embedded && showAnchorFrame ? (
            <div className="grid grid-cols-2 gap-x-6">
              <div>{anchorFrameSection}</div>
              <div>
                {renderWindowTimePortion(true)}
                {renderManualBypassSection(false)}
              </div>
            </div>
          ) : (
            <>
              {embedded ? (
                <>
                  <div className="flex items-center gap-2 pb-1">
                    <h2 className="text-md font-bold">{weekdayLabel}</h2>
                    {weekdayHelp && (
                      <Tooltip title={weekdayHelp}>
                        <InfoIcon className="text-chariot-text-secondary size-4" />
                      </Tooltip>
                    )}
                  </div>
                  {weekdaySection}
                  <div className="border-chariot-stroke my-4 border-t" />
                  {renderWindowTimePortion(false)}
                  {renderManualBypassSection(true)}
                </>
              ) : (
                <>
                  <h2 className="text-md pb-1 font-bold">
                    Days to Scan Assets
                  </h2>
                  {weekdaySection}
                  {renderTimeSection(false)}
                  {timezoneSection}
                  {renderManualBypassSection(true)}
                </>
              )}
            </>
          )}
        </Container>

        {!embedded && (
          <div className={cn('flex w-fit gap-2', customerEmail && 'ml-auto')}>
            {customerEmail && (
              <Button
                variant="outline"
                onClick={async () => {
                  onClose?.();
                }}
                data-testid="scan-schedule-cancel-button"
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={async () => {
                if (
                  start?.value &&
                  end?.value &&
                  timezone &&
                  scanDays.length > 0
                ) {
                  setSavedTimezone(timezone);
                  if (timezone.value !== 'UTC') {
                    const { utcTime: utcStartTime, diff: diffStart } =
                      convertToUTC(
                        start.value as string,
                        timezone.value as string
                      );
                    const { utcTime: utcEndTime } = convertToUTC(
                      end.value as string,
                      timezone.value as string
                    );
                    // convert days to utc days
                    const days = scanDays.map(day => {
                      return convertDayToUTC(day, diffStart);
                    });
                    await updateScanScheduleAsync({
                      days,
                      start: utcStartTime,
                      end: utcEndTime,
                      manualBypass,
                    });
                  } else {
                    await updateScanScheduleAsync({
                      days: scanDays,
                      start: start.value as string,
                      end: end.value as string,
                      manualBypass,
                    });
                  }
                  onClose?.();
                }
              }}
              variant="default"
              className="w-fit"
              disabled={!start || !end || !timezone || scanDays.length === 0}
              data-testid="scan-schedule-save-button"
            >
              Save
            </Button>
            {!customerEmail && (
              <Button
                variant="outline"
                onClick={async () => {
                  await deleteScanSchedule();
                  setScanDays([]);
                  setStart(undefined);
                  setEnd(undefined);
                  setTimezone(timezoneOptions[0]);
                }}
                data-testid="scan-schedule-reset-button"
              >
                Reset to Default
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
);
